import { Route, Switch, useLocation } from 'wouter';
import { Step } from '../application/Application';
import { OrgPartnerAssociationSelection } from '../application/organization/final-details/OrgPartnerAssociationSelection';
import { OrgSubmission } from '../application/organization/final-details/OrgSubmission';
import { OrgBusinessModelSelection } from '../application/organization/operations/OrgBusinessModelSelection';
import { OrgCountryOperationsSelection } from '../application/organization/operations/OrgCountryOperationsSelection';
import { OrgFundingSelection } from '../application/organization/operations/OrgFundingSelection';
import { OrgImpactTrackingSelection } from '../application/organization/operations/OrgImpactTrackingSelection';
import { OrgRevenueGenerationSelection } from '../application/organization/operations/OrgRevenueGenerationSelection';
import { OrgSDGSelection } from '../application/organization/operations/OrgSDGSelection';
import { OrgDetailsSelection } from '../application/organization/org-profile/OrgDetailsSelection';
import { OrgLogoUpload } from '../application/organization/org-profile/OrgLogoUpload';
import { OrgProblemSelection } from '../application/organization/org-profile/OrgProblemSelection';
import { OrgSolutionSelection } from '../application/organization/org-profile/OrgSolutionSelection';
import { OrgTypeSelection } from '../application/organization/org-profile/OrgTypeSelection';
import { OrgWebsiteSelection } from '../application/organization/org-profile/OrgWebsiteSelection';
import { OrgRepBackgroundSelection } from '../application/organization/rep-profile/OrgRepBackgroundSelection';
import { OrgRepLocationSelection } from '../application/organization/rep-profile/OrgRepLocationSelection';
import { OrgRepProfilePicUpload } from '../application/organization/rep-profile/OrgRepProfilePicUpload';
import { Organizations } from '@platform/client';
import { useApplicationLocalStorage } from '../application/application-hook';
import { OrgProfileUpdateRequest } from '@platform/client/schemas';
import { useEffect, useState } from 'react';
import { ROUTES } from '../routes/consts';

enum OrganizationProfileSteps {
  NAME_SELECTION = 'NAME_SELECTION',
  WEBSITE_SELECTION = 'WEBSITE_SELECTION',
  TYPE_SELECTION = 'TYPE_SELECTION',
  PROBLEM_SELECTION = 'PROBLEM_SELECTION',
  SOLUTION_SELECTION = 'SOLUTION_SELECTION',
  LOGO_UPLOAD = 'LOGO_UPLOAD',

  REVENUE_SELECTION = 'REVENUE_SELECTION',
  FUNDING_SELECTION = 'FUNDING_SELECTION',
  BUSINESS_MODEL_SELECTION = 'BUSINESS_MODEL_SELECTION',
  ORG_LOCATION_SELECTION = 'ORG_LOCATION_SELECTION',
  SDG_SELECTION = 'SDG_SELECTION',
  IMPACT_TRACKING_SELECTION = 'IMPACT_TRACKING_SELECTION',

  REP_LOCATION_SELECTION = 'REP_LOCATION_SELECTION',
  REP_EXPERIENCE_SELECTION = 'REP_EXPERIENCE_SELECTION',
  REP_PROFILE_PICTURE_UPLOAD = 'REP_PROFILE_PICTURE_UPLOAD',

  PARTNER_SELECTION = 'PARTNER_SELECTION',
  REFERRAL_SELECTION = 'REFERRAL_SELECTION',
}

const OrganizationProfileStep: Record<OrganizationProfileSteps, Step> = {
  [OrganizationProfileSteps.NAME_SELECTION]: {
    progress: 20,
    component: OrgDetailsSelection,
    url: '/',
  },
  [OrganizationProfileSteps.WEBSITE_SELECTION]: {
    progress: 20,
    component: OrgWebsiteSelection,
    url: '/organization-website-selection',
  },
  [OrganizationProfileSteps.TYPE_SELECTION]: {
    progress: 20,
    component: OrgTypeSelection,
    url: '/organization-type-selection',
  },
  [OrganizationProfileSteps.PROBLEM_SELECTION]: {
    progress: 20,
    component: OrgProblemSelection,
    url: '/organization-problem-selection',
  },
  [OrganizationProfileSteps.SOLUTION_SELECTION]: {
    progress: 20,
    component: OrgSolutionSelection,
    url: '/organization-solution-selection',
  },
  [OrganizationProfileSteps.LOGO_UPLOAD]: {
    progress: 90,
    component: OrgLogoUpload,
    url: '/organization-logo-upload',
  },
  [OrganizationProfileSteps.REVENUE_SELECTION]: {
    progress: 120,
    component: OrgRevenueGenerationSelection,
    url: '/organization-revenue-selection',
  },
  [OrganizationProfileSteps.FUNDING_SELECTION]: {
    progress: 120,
    component: OrgFundingSelection,
    url: '/organization-funding-selection',
  },
  [OrganizationProfileSteps.BUSINESS_MODEL_SELECTION]: {
    progress: 120,
    component: OrgBusinessModelSelection,
    url: '/organization-business-model-selection',
  },
  [OrganizationProfileSteps.ORG_LOCATION_SELECTION]: {
    progress: 120,
    component: OrgCountryOperationsSelection,
    url: '/organization-location-selection',
  },
  [OrganizationProfileSteps.SDG_SELECTION]: {
    progress: 120,
    component: OrgSDGSelection,
    url: '/organization-sdg-selection',
  },
  [OrganizationProfileSteps.IMPACT_TRACKING_SELECTION]: {
    progress: 120,
    component: OrgImpactTrackingSelection,
    url: '/organization-impact-tracking-selection',
  },
  [OrganizationProfileSteps.REP_LOCATION_SELECTION]: {
    progress: 330,
    component: OrgRepLocationSelection,
    url: '/organization-rep-location-selection',
  },
  [OrganizationProfileSteps.REP_EXPERIENCE_SELECTION]: {
    progress: 330,
    component: OrgRepBackgroundSelection,
    url: '/organization-rep-experience-selection',
  },
  [OrganizationProfileSteps.REP_PROFILE_PICTURE_UPLOAD]: {
    progress: 330,
    component: OrgRepProfilePicUpload,
    url: '/organization-rep-profile-picture-upload',
  },
  [OrganizationProfileSteps.PARTNER_SELECTION]: {
    progress: 440,
    component: OrgPartnerAssociationSelection,
    url: '/organization-partner-selection',
  },
  [OrganizationProfileSteps.REFERRAL_SELECTION]: {
    progress: 440,
    component: OrgSubmission,
    url: '/organization-referral-selection',
  },
};

export const EditProfileRootOrg = () => {
  const [location, navigate] = useLocation();
  const [initialized, setInitialized] = useState(false);
  const { data } = Organizations.useGetOrganizationProfile();
  const { getApplicationData, updateApplicationData } =
    useApplicationLocalStorage<OrgProfileUpdateRequest>();

  useEffect(() => {
    if (data) {
      const revGeneratingValue =
        data.revenueGeneration !== undefined ?
          data.revenueGeneration ?
            'revenue-generating'
          : 'non-revenue-generating'
        : undefined;
      updateApplicationData({
        name: data?.name,
        hqCountry: data?.hqCountry || undefined,
        yearOfRegistration: data?.yearOfRegistration || undefined,
        website: data?.website || undefined,
        linkedin: data?.linkedin || null,
        organizationType: data?.organizationType?.toString() || undefined,
        organizationSize: data?.organizationSize?.toString() || undefined,
        problem: data?.problem || null,
        solution: data?.solution || null,
        orgLogo: data?.orgLogo || null,
        revenueGeneration: revGeneratingValue || null,
        revenueAmount: data?.revenueAmount || null,
        funding: data?.funding || null,
        fundingAmount: data?.fundingAmount || null,
        businessModel: data?.businessModel || null,
        businessModelDescription: data?.businessModelDescription || null,
        countries: data?.countries || null, // to confirm we are discarding this on sign up
        industries: data?.industries || undefined,
        sdgs: data?.sdgs || [],
        sdgTracking: data?.sdgTracking || null,
        repCountry: data?.repCountry || undefined,
        repCity: data?.repCity || undefined,
        repTimezone: data?.repTimezone || undefined,
        repJobTitle: data?.repJobTitle || undefined,
        repLinkedin: data?.repLinkedin || null,
        repLanguages: data?.repLanguages,
        repProfilePicture: data?.repProfilePicture,
        impactPartners: data?.partners,
        otherPartners: data?.otherPartners,
        experienceHope: data?.experienceHope || null,
        referralMethod: data?.referralMethod || null,
      });
      setInitialized(true);
    }
  }, [data, updateApplicationData]);

  const orgProfileMutator = Organizations.useUpdateOrganizationProfile({
    mutation: {
      onSuccess: () => {
        navigate(`~/platform${ROUTES.DASHBOARD}`);
      },
    },
  });
  const onSubmit = () => {
    const profileData = getApplicationData();
    orgProfileMutator.mutate({ data: { ...profileData } });
  };

  // should these be pulled out?
  const goToNextStep = () => {
    const steps = Object.values(OrganizationProfileSteps);
    const currentIndex = steps.findIndex((step) => {
      return OrganizationProfileStep[step as OrganizationProfileSteps].url === location;
    });
    const nextStep =
      currentIndex < steps.length - 1 ? steps[currentIndex + 1] : steps[currentIndex];
    const nextUrl = OrganizationProfileStep[nextStep as OrganizationProfileSteps].url;
    navigate(nextUrl);
    return nextStep as OrganizationProfileSteps;
  };

  const goToPreviousStep = () => {
    const steps = Object.values(OrganizationProfileSteps);
    const currentIndex = steps.findIndex(
      (step) => OrganizationProfileStep[step as OrganizationProfileSteps].url === location,
    );
    const previousStep = currentIndex > 0 ? steps[currentIndex - 1] : steps[currentIndex];
    const previousUrl =
      currentIndex > 0 ?
        OrganizationProfileStep[previousStep as OrganizationProfileSteps].url
      : `~/platform${ROUTES.DASHBOARD}`;
    navigate(previousUrl);
    return previousStep as OrganizationProfileSteps;
  };

  if (!initialized) {
    return null;
  }

  return (
    <Switch>
      {Object.entries(OrganizationProfileStep).map(([_key, value]) => {
        const { url, component: Component, progress } = value;
        return (
          <Route key={url} path={url}>
            <Component
              onBack={goToPreviousStep}
              onContinue={goToNextStep}
              progress={progress}
              isEditProfile={true}
              onSubmit={onSubmit}
            />
          </Route>
        );
      })}
    </Switch>
  );
};
